import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";
import { usePreviewMode } from "../hooks/usePreviewMode";
import { previewHeaders } from "../lib/previewAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Community landing page — lists all published Founder Features.
// Structurally mirrors PerspectivesLanding but leans on the fact that
// Community items carry their own hero photograph, so each row becomes a
// restrained editorial card rather than a text-only entry.
export default function CommunityLanding() {
  const { t, i18n } = useTranslation();
  const preview = usePreviewMode();
  const [items, setItems] = useState([]);
  const lang = (i18n.language || "en").split("-")[0];

  useEffect(() => {
    const url = `${API}/community?lang=${lang}${preview ? "&include_drafts=true" : ""}`;
    axios
      .get(url, { headers: preview ? previewHeaders() : {} })
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]));
  }, [lang, preview]);

  useEffect(() => {
    const t0 = document.title;
    document.title = "Community — The Thriving Life 101";
    return () => {
      document.title = t0;
    };
  }, []);

  return (
    <div className="min-h-screen text-primary-custom" data-testid="community-landing">
      <header className="max-w-4xl mx-auto px-6 md:px-12 pt-24 pb-16 text-center">
        <span className="text-[10px] font-sans tracking-[0.4em] uppercase text-[var(--accent-bronze)] font-semibold block">
          {t("community.landing.eyebrow")}
        </span>
        <h1 className="mt-6 font-serif tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
          {t("community.landing.title")}
        </h1>
        <p className="mt-6 font-serif text-lg md:text-xl italic text-secondary-custom leading-relaxed font-light max-w-2xl mx-auto">
          {t("community.landing.intro")}
        </p>
      </header>

      <div className="max-w-6xl mx-auto px-6 md:px-12 pb-32">
        {items.length === 0 ? (
          <div
            className="max-w-2xl mx-auto text-center border-t border-b border-subtle-custom py-16"
            data-testid="community-landing-empty"
          >
            <p className="font-serif text-base md:text-lg italic text-secondary-custom leading-relaxed">
              {t("community.landing.empty")}
            </p>
          </div>
        ) : (
          <ul className="space-y-16 border-t border-subtle-custom pt-16">
            {items.map((item) => {
              const hero = item.hero || {};
              const subject = item.subject || {};
              const seriesLabel = [item.series, item.seriesNumber].filter(Boolean).join(" ");
              return (
                <li key={item.slug} data-testid={`community-landing-item-${item.slug}`}>
                  <Link
                    to={`/community/${item.slug}`}
                    data-testid={`community-landing-link-${item.slug}`}
                    className="group grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center"
                  >
                    {hero.image && (
                      <div className="md:col-span-5">
                        <div className="aspect-[4/5] overflow-hidden border border-subtle-custom">
                          <img
                            src={hero.image}
                            alt={hero.alt || item.title}
                            loading="lazy"
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
                          />
                        </div>
                      </div>
                    )}
                    <div className={`${hero.image ? "md:col-span-7" : "md:col-span-12"} space-y-5`}>
                      {seriesLabel && (
                        <span className="text-[10px] font-sans tracking-[0.35em] uppercase text-[var(--accent-bronze)] font-semibold block">
                          {seriesLabel}
                        </span>
                      )}
                      {item.status === "draft" && (
                        <span className="text-[9px] font-sans tracking-[0.3em] uppercase text-secondary-custom/60 block">
                          Draft
                        </span>
                      )}
                      <h2 className="font-serif tracking-tight text-2xl md:text-3xl lg:text-4xl text-primary-custom group-hover:text-[var(--accent-bronze)] transition-colors leading-tight">
                        {item.title}
                      </h2>
                      {item.subtitle && (
                        <p className="font-serif text-base md:text-lg italic text-secondary-custom leading-relaxed font-light">
                          {item.subtitle}
                        </p>
                      )}
                      {subject.name && (
                        <p className="text-[11px] font-sans tracking-[0.2em] uppercase text-secondary-custom">
                          {subject.name}
                          {subject.role ? ` · ${subject.role}` : ""}
                        </p>
                      )}
                      <span className="inline-flex items-center gap-2 text-[10px] font-sans tracking-[0.25em] uppercase text-primary-custom font-semibold group-hover:text-[var(--accent-bronze)] transition-colors pt-2">
                        <span>{t("community.landing.readFeature")}</span>
                        <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                      </span>
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
